import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useVoice } from '../../contexts/VoiceContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import VoiceInterface from './VoiceInterface';
import { ArrowLeft, X } from '@phosphor-icons/react';

const friendlyErrorMessage = (error) => {
  if (!error) return '';
  if (error.toLowerCase().includes('failed to fetch') || error.toLowerCase().includes('network'))
    return 'Could not connect. Please check your connection and try again.';
  return error;
};

const ChatArea = ({
  currentSessionId,
  messages,
  loading,
  error,
  onSendMessage
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { currentSessionMeta, setMessageFeedback, clearError, retryLastMessage, isStreaming, stopStreaming, createSession } = useChat();
  const { isVoiceMode, startVoice, stopVoice } = useVoice();

  const isStudent = currentUser?.user_type === 'student';
  const gradeLevel = currentUser?.grade_level || 'middle';
  const isElementary = gradeLevel === 'elementary';

  const sessionTitle = currentSessionMeta?.title || 'New Chat';
  const messageCount = messages?.length || 0;

  const handleVoiceToggle = async () => {
    if (isVoiceMode) {
      stopVoice();
    } else {
      let sessionId = currentSessionId;
      if (!sessionId) {
        const newSession = await createSession();
        sessionId = newSession?.id;
      }
      if (sessionId) {
        startVoice(sessionId);
      }
    }
  };

  return (
    <div id="main-content" className="flex-1 flex flex-col h-full relative overflow-hidden">
      <div className="chat-container flex flex-col h-full overflow-hidden bg-white rounded-lg">
        {/* Enhanced chat header */}
        <div className="chat-header shadow-sm py-3 md:py-4 px-4 md:px-6 flex items-center justify-between md:mt-0 mt-8">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {isStudent && (
              <button
                onClick={() => navigate('/student/dashboard')}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                aria-label="Back to dashboard"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {isElementary ? (
                  <h2 className="text-lg font-bold text-indigo-600 truncate">
                    {sessionTitle === 'New Chat' ? 'Your Chat' : sessionTitle}
                  </h2>
                ) : (
                  <h2 className="text-base font-semibold text-gray-800 truncate">
                    {sessionTitle}
                  </h2>
                )}
              </div>
              {messageCount > 0 && (
                <p className="text-xs text-gray-400 mt-0.5">
                  {messageCount} {messageCount === 1 ? 'message' : 'messages'}
                </p>
              )}
            </div>
          </div>
          <div className="logo-container flex-shrink-0">
            <span className="text-xl font-bold text-indigo-600">Brillabee</span>
          </div>
        </div>

        <MessageList
          messages={messages}
          loading={loading}
          sessionId={currentSessionId}
          onFeedback={setMessageFeedback}
          gradeLevel={gradeLevel}
        />

        {/* Error banner - flow-based between messages and input */}
        {error && (
          <div className="mx-4 mb-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 animate-fade-in-up">
            <div className="flex-1 text-sm text-red-700">
              {friendlyErrorMessage(error)}
            </div>
            <button
              onClick={retryLastMessage}
              className="px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors flex-shrink-0"
              aria-label="Try again"
            >
              Try Again
            </button>
            <button
              onClick={clearError}
              className="p-1 text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
              aria-label="Dismiss error"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {isVoiceMode ? (
          <VoiceInterface
            gradeLevel={gradeLevel}
            onExitVoice={() => stopVoice()}
          />
        ) : (
          <MessageInput
            onSendMessage={onSendMessage}
            disabled={loading}
            isStreaming={isStreaming}
            onStopStreaming={stopStreaming}
            gradeLevel={gradeLevel}
            onVoiceToggle={handleVoiceToggle}
          />
        )}
      </div>
    </div>
  );
};

export default ChatArea;
